// Roles Tab Component for AdminPage
import React, { useState } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { availablePermissions } from './constants';
import * as api from '../../services/api';

const RolesTab = ({ roles, setRoles }) => {
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [newRole, setNewRole] = useState({ name: '', description: '', permissions: [], level: 2 });

  const handleAddRole = async () => {
    try {
      const response = await api.createRole(newRole);
      setRoles([...roles, response.data]);
      setShowAddRoleModal(false);
      setNewRole({ name: '', description: '', permissions: [], level: 2 });
    } catch (error) {
      console.error('Error creating role:', error);
      alert('Error al crear rol: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditRole = async () => {
    try {
      const response = await api.updateRole(selectedRole.id, selectedRole);
      setRoles(roles.map(r => r.id === selectedRole.id ? response.data : r));
      setShowEditRoleModal(false);
      setSelectedRole(null);
    } catch (error) {
      console.error('Error updating role:', error);
      alert('Error al actualizar rol: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteRole = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este rol?')) {
      try {
        await api.deleteRole(id);
        setRoles(roles.filter(r => r.id !== id));
      } catch (error) {
        console.error('Error deleting role:', error);
        alert('Error al eliminar rol');
      }
    }
  };

  const togglePermission = (permission, isEdit = false) => {
    if (isEdit && selectedRole) {
      const perms = selectedRole.permissions || [];
      const newPerms = perms.includes(permission) 
        ? perms.filter(p => p !== permission)
        : [...perms, permission];
      setSelectedRole({ ...selectedRole, permissions: newPerms });
    } else {
      const perms = newRole.permissions || [];
      const newPerms = perms.includes(permission)
        ? perms.filter(p => p !== permission)
        : [...perms, permission];
      setNewRole({ ...newRole, permissions: newPerms });
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-gray-700">Roles y Permisos</CardTitle>
          <Button onClick={() => setShowAddRoleModal(true)} className="bg-[#D4A843] hover:bg-[#c49a3a]" data-testid="add-role-btn">
            <Plus size={18} className="mr-2" />
            Agregar Rol
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roles.map((role) => (
              <Card key={role.id} className="border">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-800">{role.name}</h3>
                      <p className="text-sm text-gray-500">{role.description}</p>
                    </div>
                    <Badge variant="outline">Nivel {role.level}</Badge>
                  </div>
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-2">Permisos:</p>
                    <div className="flex flex-wrap gap-1">
                      {(role.permissions || []).map((perm) => (
                        <Badge key={perm} variant="secondary" className="text-xs">
                          {availablePermissions.find(p => p.id === perm)?.label || perm}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end space-x-2">
                    <Button size="sm" variant="ghost" onClick={() => { setSelectedRole({...role}); setShowEditRoleModal(true); }}>
                      <Edit size={14} className="mr-1" /> Editar
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteRole(role.id)}>
                      <Trash2 size={14} className="mr-1" /> Eliminar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Add Role Modal */}
      <Dialog open={showAddRoleModal} onOpenChange={setShowAddRoleModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Agregar Rol</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Nombre del Rol</label>
              <Input value={newRole.name} onChange={(e) => setNewRole({...newRole, name: e.target.value})} placeholder="Ej: Gerente de Ventas" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Descripción</label>
              <Input value={newRole.description} onChange={(e) => setNewRole({...newRole, description: e.target.value})} placeholder="Breve descripción del rol" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Nivel (0=Admin, 1=Supervisor, 2=Usuario)</label>
              <Input type="number" min="0" max="10" value={newRole.level} onChange={(e) => setNewRole({...newRole, level: parseInt(e.target.value)})} />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Permisos</label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                {availablePermissions.map((perm) => (
                  <div key={perm.id} className="flex items-center space-x-2">
                    <Checkbox id={`new-${perm.id}`} checked={(newRole.permissions || []).includes(perm.id)} onCheckedChange={() => togglePermission(perm.id, false)} />
                    <label htmlFor={`new-${perm.id}`} className="text-sm cursor-pointer">
                      <span className="font-medium">{perm.label}</span>
                      <span className="block text-xs text-gray-500">{perm.description}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRoleModal(false)}>Cancelar</Button>
            <Button onClick={handleAddRole} className="bg-[#D4A843] hover:bg-[#c49a3a]">Crear Rol</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Modal */}
      <Dialog open={showEditRoleModal} onOpenChange={setShowEditRoleModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Rol</DialogTitle>
          </DialogHeader>
          {selectedRole && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Nombre del Rol</label>
                <Input value={selectedRole.name} onChange={(e) => setSelectedRole({...selectedRole, name: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Descripción</label>
                <Input value={selectedRole.description} onChange={(e) => setSelectedRole({...selectedRole, description: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Nivel</label>
                <Input type="number" min="0" max="10" value={selectedRole.level} onChange={(e) => setSelectedRole({...selectedRole, level: parseInt(e.target.value)})} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Permisos</label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                  {availablePermissions.map((perm) => (
                    <div key={perm.id} className="flex items-center space-x-2">
                      <Checkbox id={`edit-${perm.id}`} checked={(selectedRole.permissions || []).includes(perm.id)} onCheckedChange={() => togglePermission(perm.id, true)} />
                      <label htmlFor={`edit-${perm.id}`} className="text-sm cursor-pointer">
                        <span className="font-medium">{perm.label}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditRoleModal(false)}>Cancelar</Button>
            <Button onClick={handleEditRole} className="bg-[#D4A843] hover:bg-[#c49a3a]">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RolesTab;
